import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import MapScriptLoader from 'bundles/connect/helpers/map_script_loader';

import {
  GoogleMap,
  Polygon,
  DrawingManager,
  StandaloneSearchBox
} from '@react-google-maps/api';

const DEFAULT_CENTER = { lat: 38, lng: -92 };
const DEFAULT_ZOOM = 4;

export default function PolygonMap({ fitBoundsDelay = 0, geoJSON, googleMapsApiKey, onPolygonComplete }) {
  let polygon;
  if (!isEmpty(geoJSON)) {
    const coordinates = geoJSON.coordinates[0];
    polygon = coordinates.map(c => ({ lat: c[1], lng: c[0] }));
  }

  const [map, setMap] = useState(null);
  const [searchBox, setSearchBox] = useState(null);

  useEffect(() => {
    if (!map || !polygon) return;

    const bounds = new google.maps.LatLngBounds();
    polygon.map(coord => bounds.extend(coord));

    if (fitBoundsDelay === 0) {
      map.fitBounds(bounds);
    } else {
      setTimeout(() => {
        map.fitBounds(bounds);
      }, fitBoundsDelay);
    }
  }, [map, geoJSON]);

  const handlePlacesChanged = () => {
    const place = searchBox.getPlaces()[0];

    if (!place || !place.geometry || !map) return;

    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport);
    } else {
      map.setCenter(place.geometry.location);
      map.setZoom(17);
    }
  };

  const handlePolygonComplete = (polygon) => {
    const coordinates = [];
    const points = polygon.getPath().getArray();

    // We only care about polygons with area. Discard single or double point polygons
    if (points.length < 3) {
      polygon.setMap(null);
      return;
    }

    points.forEach((point) => {
      const lat = point.lat();
      const lng = point.lng();

      coordinates.push([lng, lat]);
    });
    coordinates.push([...coordinates[0]]);

    const geoJSON = {
      type: 'Polygon',
      coordinates: [coordinates] // intentional double wrapping
    };

    polygon.setMap(null);
    onPolygonComplete(geoJSON);
  };

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  return (
    <MapScriptLoader googleMapsApiKey={googleMapsApiKey}>
      <GoogleMap
        center={DEFAULT_CENTER}
        mapContainerStyle={{ width: '100%', height: '400px' }}
        options={{ mapTypeControl: false }}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <StandaloneSearchBox
          onLoad={ref => setSearchBox(ref)}
          onPlacesChanged={handlePlacesChanged}
        >
          <input
            placeholder="Enter a location"
            style={{
              boxSizing: 'border-box',
              border: '1px solid transparent',
              width: '240px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '3px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
              fontSize: '14px',
              outline: 'none',
              textOverflow: 'ellipses',
              position: 'absolute',
              left: '10px',
              top: '10px'
            }}
            type="text"
          />
        </StandaloneSearchBox>
        {polygon && <Polygon path={polygon} />}
        <DrawingManager
          drawingMode={get(window.google, 'maps.drawing.OverlayType.POLYGON')}
          options={{
            drawingControl: true,
            drawingControlOptions: {
              position: get(window.google, 'maps.ControlPosition.RIGHT_TOP'),
              drawingModes: [
                get(window.google, 'maps.drawing.OverlayType.POLYGON')
              ]
            }
          }}
          onPolygonComplete={handlePolygonComplete}
        />
      </GoogleMap>
    </MapScriptLoader>
  );
}

PolygonMap.propTypes = {
  fitBoundsDelay: PropTypes.number,
  geoJSON: PropTypes.object,
  googleMapsApiKey: PropTypes.string.isRequired,
  onPolygonComplete: PropTypes.func.isRequired,
};
